import { useQuery } from '@tanstack/react-query'
import { Meilisearch } from 'meilisearch'
import { useUserSearchToken } from '~/api/queries/user'
import type {
  AddressBookHit,
  AddressBookType,
  TypedAddress
} from '~/domains/actions/types'
import { useConfig } from '~/domains/configuration/queries/config'
import { isValidEmail } from '~/utils/email'

/**
 * The Meilisearch shared index stores French `type` labels (they double as the
 * global-search facet labels). That vocabulary must not leak past this module:
 * filters are translated on the way in, hits normalized on the way out.
 */
const INDEX_TYPE: Record<AddressBookType, string> = {
  user: 'Utilisateur',
  applicant: 'Candidat',
  contact: 'Contact',
  external: 'Externe'
}

const typeFromIndex = (raw: string): AddressBookType | null => {
  switch (raw) {
    case 'Utilisateur':
      return 'user'
    case 'Candidat':
      return 'applicant'
    case 'Contact':
      return 'contact'
    case 'Externe':
      return 'external'
    default:
      return null
  }
}

const IRI_PATH: Record<AddressBookType, string | null> = {
  user: 'users',
  applicant: 'applicants',
  contact: 'contacts',
  external: null
}

/**
 * The value sent to the API: an IRI for address-book entities, the raw email for
 * external recipients (the backend resolves raw emails on its own).
 */
export const addressBookHitValue = (hit: AddressBookHit): string => {
  const path = IRI_PATH[hit.type]
  return path ? `/v1/${path}/${hit.id}` : hit.email
}

/**
 * Parse a free-typed recipient, accepting a bare email ("contact@google.fr")
 * or the "Name <email>" form ("vincent <contact@google.fr>"). Returns null if invalid.
 */
export const parseTypedAddress = (input: string): TypedAddress | null => {
  const trimmed = input.trim()
  if (trimmed === '') {
    return null
  }
  const named = trimmed.match(/^(.*?)\s*<\s*([^<>]+?)\s*>$/)
  if (named) {
    const name = (named[1] ?? '').trim()
    const email = (named[2] ?? '').trim()
    return isValidEmail(email)
      ? { raw: trimmed, name: name === '' ? undefined : name, email }
      : null
  }
  return isValidEmail(trimmed) ? { raw: trimmed, email: trimmed } : null
}

type UseAddressBookSearchParams = {
  term: string
  types?: AddressBookType[]
  orderCompanyId?: number
  enabled?: boolean
}

export const useAddressBookSearch = ({
  term,
  types,
  orderCompanyId,
  enabled = true
}: UseAddressBookSearchParams) => {
  const { data: searchEndpoint } = useConfig('organization.search_endpoint')
  const { data: slug } = useConfig('organization.slug')
  const { data: searchToken } = useUserSearchToken()
  const instanceName = slug?.replace('.', '-') ?? ''

  return useQuery({
    queryKey: ['addressbook', instanceName, term, types, orderCompanyId],
    queryFn: async () => {
      if (searchEndpoint === undefined || searchToken === undefined) {
        return []
      }
      const client = new Meilisearch({
        host: searchEndpoint,
        apiKey: searchToken.token
      })
      const filters: string[] = []
      if (types && types.length > 0) {
        filters.push(
          `(${types.map((type) => `type = ${INDEX_TYPE[type]}`).join(' OR ')})`
        )
      }
      if (orderCompanyId !== undefined) {
        // In an order context, contacts are restricted to the order's company.
        filters.push(
          `(type != ${INDEX_TYPE.contact} OR company_id = ${orderCompanyId})`
        )
      }
      const response = await client
        .index(`${instanceName}_items`)
        .search<Omit<AddressBookHit, 'type'> & { type: string }>(term, {
          limit: 20,
          filter: filters.length > 0 ? filters.join(' AND ') : undefined
        })
      const hits: AddressBookHit[] = []
      for (const hit of response.hits) {
        const type = typeFromIndex(hit.type)
        if (type !== null) {
          hits.push({ ...hit, type })
        }
      }
      return hits
    },
    // An empty query is allowed: Meilisearch returns the top documents, so the dropdown
    // already shows a few suggestions on open (like the global search).
    enabled:
      enabled &&
      instanceName !== '' &&
      searchEndpoint !== undefined &&
      searchToken !== undefined,
    staleTime: 60_000
  })
}
